import { ArrowRight, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { rankToolSearch } from '../catalog';
import { categoryLabels, tools } from '../registry';
import { taskDefinitions } from '../taskEntries';
import { toolboxUrl } from '../routing';

interface ToolCommandPaletteProps {
  onClose: () => void;
  onNavigate: (path: string) => void;
  open: boolean;
}

export function ToolCommandPalette({ onClose, onNavigate, open }: ToolCommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const results = useMemo(() => rankToolSearch([...tools, ...taskDefinitions], query, 9), [query]);

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setQuery('');
    setActiveIndex(0);
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => { window.cancelAnimationFrame(frame); document.body.style.overflow = overflow; if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true }); };
  }, [open]);

  useEffect(() => setActiveIndex(0), [query]);
  useEffect(() => { if (open) resultsRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' }); }, [open, activeIndex, results]);

  if (!open) return null;

  function choose(index: number) {
    const tool = results[index];
    if (!tool) return;
    onNavigate(toolboxUrl(tool.slug));
    onClose();
  }

  return <div className="tool-command-backdrop" onPointerDown={(event) => { if (event.target === event.currentTarget) onClose(); }} role="presentation">
    <section ref={dialogRef} aria-label="搜索工具" aria-modal="true" className="tool-command" onKeyDown={(event) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onClose(); }
      if (event.key === 'Tab') {
        const controls = [...(dialogRef.current?.querySelectorAll<HTMLElement>('input, button:not([tabindex="-1"])') || [])];
        const target = controls[event.shiftKey ? controls.length - 1 : 0];
        if (event.shiftKey ? document.activeElement === controls[0] : document.activeElement === controls.at(-1)) { event.preventDefault(); target?.focus(); }
      }
      if (event.key === 'ArrowDown') { event.preventDefault(); setActiveIndex((value) => Math.max(0, Math.min(value + 1, results.length - 1))); }
      if (event.key === 'ArrowUp') { event.preventDefault(); setActiveIndex((value) => Math.max(value - 1, 0)); }
      if (event.key === 'Enter' && event.target === inputRef.current) { event.preventDefault(); choose(activeIndex); }
    }} role="dialog">
      <header><Search size={19} /><input aria-label="输入工具名称或用途" aria-autocomplete="list" aria-controls="tool-command-list" aria-expanded={open} aria-activedescendant={results[activeIndex] ? `command-result-${results[activeIndex].id}` : undefined} role="combobox" maxLength={120} onChange={(event) => setQuery(event.target.value)} placeholder="搜索工具、格式或使用场景…" ref={inputRef} value={query} /><button aria-label="关闭搜索" onClick={onClose} type="button"><X size={17} /></button></header>
      <div ref={resultsRef} id="tool-command-list" role="listbox" aria-label="匹配工具" className="tool-command-results">
        {results.map((tool, index) => <button role="option" aria-selected={index === activeIndex} id={`command-result-${tool.id}`} tabIndex={-1} className={index === activeIndex ? 'is-active' : ''} key={tool.id} onClick={() => choose(index)} onMouseEnter={() => setActiveIndex(index)} type="button"><span><strong>{tool.title}</strong><small>{tool.entryKind === 'task' ? '具体任务' : categoryLabels[tool.category]} · {tool.summary}</small></span><ArrowRight size={15} /></button>)}
        {!results.length && <div className="tool-command-empty"><Search size={22} /><strong>没有找到匹配工具</strong><span>试试输入用途，例如“图片压缩”“JSON”“证书”。</span></div>}
      </div>
      <footer><span><kbd>↑</kbd><kbd>↓</kbd> 选择</span><span><kbd>Enter</kbd> 打开</span><span><kbd>Esc</kbd> 关闭</span></footer>
    </section>
  </div>;
}
